import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "~/lib/utils";

const buttonVariants = cva(
  "body-sm-md inline-flex rounded-[12px] justify-center items-center !box-border disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "text-token-primary-onPrimary bg-token-primary hover:bg-token-primary-hover active:bg-token-primary-active disabled:text-neutral-900 disabled:bg-neutral-300",
        primary:
          "bg-token-secondary text-token-secondary-onSecondary hover:bg-token-secondary-hover active:bg-token-secondary-hover disabled:text-neutral-900 disabled:bg-neutral-300",
        secondary:
          "border hover:outline hover:outline-1 hover:outline-violet-700 border-violet-700 text-violet-700 active:bg-violet-100 -outline-offset-2 disabled:border-neutral-300 disabled:outline-transparent disabled:bg-transparent disabled:text-neutral-700",
        chat: "rounded-bl-none border hover:outline hover:outline-1 hover:outline-violet-700 border-violet-700 text-violet-700 active:bg-violet-100 -outline-offset-2 disabled:outline-transparent disabled:bg-transparent disabled:border-neutral-900 disabled:text-neutral-900",
        ghost:
          "rounded-[24px] text-secondary-hover hover:text-secondary hover:bg-violet-100 active:bg-[#EED9FB] disabled:text-neutral-800 disabled:bg-neutral-100",
        link: "text-violet-700 disabled:text-neutral-800",
        // TODO: This is a temporary solution for the danger button. Needs to be removed once the design system is updated.
        danger: "text-token-error hover:text-white border border-token-error hover:bg-token-error",
      },
      size: {
        default: "px-6 py-2.5",
        icon: "py-2 px-4 gap-x-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
